import { useState, useEffect } from "react";
import MiniCupBanner from "../../components/football/MiniCupBanner";
import { useNavigate } from "react-router-dom";
import { Trophy, Clock, ChevronRight, Users, Target, BarChart3, ShoppingBag } from "lucide-react";
import { useFb, useCountdown, formatMatchDay } from "../../utils/football";
import { useAuth } from "../../contexts/AuthContext";
import FootballDailyReward from "../../components/football/FootballDailyReward";
import FootballReferralCard from "../../components/football/FootballReferralCard";
import { FootieBall } from "../../components/football/FootieIcon";
import { FootieIntroModal, FootballOnboardingModal } from "../../components/football/FootieModals";

const MatchCard = ({ fx }) => (
  <div className="min-w-[150px] shrink-0 rounded-xl border border-border bg-card p-3" data-testid={`home-fixture-${fx.id}`}>
    <div className="mb-2 text-[11px] font-bold text-muted-foreground">{fx.status === "NS" ? "Upcoming" : fx.status}</div>
    <div className="flex items-center justify-between text-sm font-bold">
      <span className="truncate">{fx.home_team?.name}</span>
      <span className="wc-gold">{fx.home_score ?? "-"}</span>
    </div>
    <div className="mt-1 flex items-center justify-between text-sm font-bold">
      <span className="truncate">{fx.away_team?.name}</span>
      <span className="wc-gold">{fx.away_score ?? "-"}</span>
    </div>
  </div>
);

const SquadWidget = ({ squad, navigate }) => {
  const dayPath = squad?.match_day ? `/football/squad-builder/${squad.match_day}` : "/football/squad-builder";
  let title = "Build Your Squad", sub = "Pick your XI before kickoff", cta = "Build Now", to = "/football/squad-builder";
  if (squad && squad.scoring_finalized) { title = "Match Day Results"; sub = `${squad.total_points} pts`; cta = "View Results"; to = `/football/results/${squad.match_day}`; }
  else if (squad && squad.is_locked) { title = "Squad Locked ✓"; sub = "Good luck! Points update live"; cta = "View Squad"; to = dayPath; }
  else if (squad) { title = "Finish Your Squad"; sub = "Draft saved — not locked yet"; cta = "Continue"; to = dayPath; }
  return (
    <button onClick={() => navigate(to)} className="flex w-full items-center justify-between rounded-xl border border-border bg-card p-4 text-left" data-testid="home-squad-widget">
      <div className="flex items-center gap-3">
        <Users size={22} className="wc-gold" />
        <div><div className="font-bold">{title}</div><div className="text-xs text-muted-foreground">{sub}</div></div>
      </div>
      <span className="rounded-lg wc-bg-gold px-3 py-1.5 text-sm font-extrabold text-slate-900">{cta}</span>
    </button>
  );
};

export default function FootballHomePage() {
  const fb = useFb();
  const navigate = useNavigate();
  const { dbUser, refreshDbUser } = useAuth();
  const [fixtures, setFixtures] = useState([]);
  const [squad, setSquad] = useState(null);
  const [lb, setLb] = useState([]);
  const [me, setMe] = useState(null);
  const [modal, setModal] = useState(null); // "onboarding" | "footie" | null

  const nextKickoff = fixtures.find((f) => f.status === "NS")?.kickoff_utc;
  const cd = useCountdown(nextKickoff);

  // One-time intros: football mode onboarding first, then FOOTIE intro.
  useEffect(() => {
    if (!dbUser) return;
    if (!dbUser.football_onboarding_seen) setModal("onboarding");
    else if (!dbUser.footie_intro_seen) setModal("footie");
  }, [dbUser]);

  const dismissModal = async (flag, next) => {
    setModal(next);
    try { await fb.post("/football/onboarding/seen", { flag }); refreshDbUser?.(); } catch { /* ignore */ }
  };

  // Has the user already drafted/locked a squad for the upcoming match day?
  const hasDraft = !!(squad && ((squad.squad || []).length > 0 || squad.is_locked || squad.scoring_finalized));
  const footieDay = squad?.match_day;

  useEffect(() => {
    if (!dbUser) return;
    const load = async () => {
      try { const d = await fb.get("/football/fixtures/today"); setFixtures(d.fixtures || []); } catch {}
    };
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dbUser]);

  useEffect(() => {
    if (!dbUser) return;
    (async () => {
      try { setSquad(await fb.get("/football/squad/today")); } catch { setSquad(null); }
      try { const d = await fb.get("/football/leaderboard/cumulative", { limit: 5 }); setLb(d.entries || []); } catch {}
      try { setMe(await fb.get("/football/leaderboard/me")); } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dbUser]);

  return (
    <div className="football-theme pb-10" data-testid="football-home">
      <MiniCupBanner />
      {modal === "onboarding" && <FootballOnboardingModal onDismiss={() => dismissModal("football_onboarding_seen", dbUser?.footie_intro_seen ? null : "footie")} />}
      {modal === "footie" && <FootieIntroModal onDismiss={() => dismissModal("footie_intro_seen", null)} />}

      {/* Hero */}
      <div className="wc-hero-gradient rounded-2xl p-5">
        <div className="flex items-center gap-2 text-amber-300"><Trophy size={18} /><span className="text-sm font-bold">Football World Cup 2026</span></div>
        <h1 className="mt-1 text-2xl font-extrabold text-white">Your Fantasy World Cup</h1>
        <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-black/30 px-3 py-1.5 text-sm font-bold text-white" data-testid="home-countdown">
          <Clock size={15} className="text-amber-300" />
          {nextKickoff ? <>Next deadline in {cd.label}</> : "No matches today"}
        </div>
      </div>

      {/* Today matches */}
      {fixtures.length > 0 && (
        <div className="mt-5">
          <h2 className="mb-2 text-base font-bold">Today · {formatMatchDay(fixtures[0]?.match_day)}</h2>
          <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-1" data-testid="home-fixtures-strip">
            {fixtures.map((fx) => <MatchCard key={fx.id} fx={fx} />)}
          </div>
        </div>
      )}

      {/* My squad */}
      <div className="mt-5"><SquadWidget squad={squad} navigate={navigate} /></div>

      {/* FOOTIE card — permanent whenever the user has no draft for the upcoming match day */}
      {!hasDraft && (
        <button
          onClick={() => navigate(footieDay ? `/football/squad-builder/${footieDay}` : "/football/squad-builder")}
          className="mt-3 flex w-full items-center justify-between rounded-xl border border-amber-400/40 bg-amber-400/10 p-4 text-left"
          data-testid="home-footie-card"
        >
          <div className="flex items-center gap-3">
            <div className="footie-ball-bounce"><FootieBall size={34} /></div>
            <div>
              <div className="font-extrabold text-amber-100">Skip the picking</div>
              <div className="text-xs text-amber-200/80">Use FOOTIE — make your team in 5 seconds</div>
            </div>
          </div>
          <span className="flex items-center gap-1 rounded-lg wc-bg-gold px-3 py-1.5 text-sm font-extrabold text-slate-900">
            <FootieBall size={16} /> Build
          </span>
        </button>
      )}

      {/* Power-Ups Shop promo — permanent */}
      <button
        onClick={() => navigate("/subscribe")}
        className="mt-3 flex w-full items-center justify-between rounded-xl border border-amber-400/30 bg-card p-4 text-left"
        data-testid="home-shop-card"
      >
        <div className="flex items-center gap-3">
          <div className="grid h-9 w-9 place-items-center rounded-lg bg-amber-400/15"><ShoppingBag size={20} className="wc-gold" /></div>
          <div>
            <div className="font-extrabold text-foreground">Power up your squad</div>
            <div className="text-xs text-muted-foreground">12th player? Negative-points shield? Browse the Shop.</div>
          </div>
        </div>
        <span className="flex shrink-0 items-center gap-1 rounded-lg wc-bg-gold px-3 py-1.5 text-sm font-extrabold text-slate-900">
          Open Shop
        </span>
      </button>

      {/* Daily reward (football-only, independent streak) */}
      <div className="mt-3"><FootballDailyReward /></div>

      {/* Quick actions */}
      <div className="mt-3 grid grid-cols-2 gap-3">
        <button onClick={() => navigate("/football/predictor")} className="flex items-center gap-2 rounded-xl border border-border bg-card p-4 font-bold" data-testid="home-predictor-btn">
          <Target size={20} className="wc-gold" /> Predictor
        </button>
        <button onClick={() => navigate("/football/leaderboard")} className="flex items-center gap-2 rounded-xl border border-border bg-card p-4 font-bold" data-testid="home-leaderboard-btn">
          <BarChart3 size={20} className="wc-gold" /> Leaderboard
        </button>
      </div>

      {/* Leaderboard snippet */}
      <div className="mt-5 rounded-xl border border-border bg-card p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-bold">Global Top 5</h2>
          <button onClick={() => navigate("/football/leaderboard")} className="flex items-center text-sm wc-gold" data-testid="home-lb-viewall">View all <ChevronRight size={16} /></button>
        </div>
        {lb.length === 0 ? <div className="py-3 text-sm text-muted-foreground">No rankings yet — be the first!</div> : (
          <div className="space-y-2">
            {lb.map((e) => (
              <div key={e.user_id} className="flex items-center justify-between text-sm" data-testid={`home-lb-row-${e.rank}`}>
                <span className="flex items-center gap-2"><span className="w-5 font-bold text-muted-foreground">{e.rank}</span>{e.display_name}{e.manager_of_day_count > 0 && " 👑"}</span>
                <span className="font-bold wc-gold">{e.points}</span>
              </div>
            ))}
            {me?.rank && !lb.find((e) => e.is_current_user) && (
              <div className="mt-1 flex items-center justify-between border-t border-border pt-2 text-sm font-bold" data-testid="home-lb-me">
                <span className="flex items-center gap-2"><span className="w-5">{me.rank}</span>{me.entry?.display_name} (you)</span>
                <span className="wc-gold">{me.entry?.points}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Refer & Earn (dual-sport rewards) */}
      <div className="mt-5"><FootballReferralCard /></div>
    </div>
  );
}
